import React from "react";

/**
 * TempleQuote — branded pull-quote component for Temple Franklin's signature lines.
 *
 * Design intent:
 *  - Quotes land like punctuation on the page, not decoration.
 *  - Serif quote, ornamental rule, attribution.
 *  - Two variants: "hero" (large, standalone section break) | "inline" (smaller, in-flow)
 *  - Two themes: "cream" (default, light backgrounds) | "teal" (dark teal section)
 *
 * Props:
 *  - quote: string (required) — the words
 *  - variant: "hero" | "inline" (default "hero")
 *  - theme: "cream" | "teal" (default "cream")
 *  - id: optional test/anchor id
 */
export const TempleQuote = ({
  quote,
  variant = "hero",
  theme = "cream",
  id,
}) => {
  const isHero = variant === "hero";
  const isTeal = theme === "teal";

  // Color tokens
  const bg = isTeal ? "#042F2E" : "transparent";
  const quoteColor = isTeal ? "#FFFFFF" : "#042F2E";
  const accentColor = isTeal ? "#d4af6a" : "#25a1c2";
  const attributionColor = isTeal ? "rgba(255,255,255,0.65)" : "#068fa2";
  const ruleColor = isTeal ? "rgba(212, 175, 106, 0.4)" : "rgba(37, 161, 194, 0.35)";

  // Sizing
  const quoteSize = isHero
    ? "text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
    : "text-xl sm:text-2xl md:text-3xl";
  const padding = isHero ? "py-20 md:py-28" : "py-10 md:py-14";

  return (
    <figure
      data-testid={id || "temple-quote"}
      className={`${padding} ${isTeal ? "" : ""}`}
      style={{ backgroundColor: bg }}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        {/* Top ornamental rule */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <span
            className="block h-px w-12 md:w-20"
            style={{ backgroundColor: ruleColor }}
            aria-hidden="true"
          />
          <span
            className="text-lg"
            style={{ color: accentColor }}
            aria-hidden="true"
          >
            ✦
          </span>
          <span
            className="block h-px w-12 md:w-20"
            style={{ backgroundColor: ruleColor }}
            aria-hidden="true"
          />
        </div>

        {/* The quote */}
        <blockquote
          className={`font-serif ${quoteSize} leading-[1.15] tracking-tight italic`}
          style={{ color: quoteColor }}
        >
          <span aria-hidden="true" style={{ color: accentColor }}>
            &ldquo;
          </span>
          {quote}
          <span aria-hidden="true" style={{ color: accentColor }}>
            &rdquo;
          </span>
        </blockquote>

        {/* Attribution */}
        <figcaption className="mt-8 md:mt-10">
          <p
            className="font-sans text-[10px] sm:text-[11px] tracking-[0.35em] uppercase font-medium"
            style={{ color: attributionColor }}
          >
            — Temple Franklin
          </p>
          <p
            className="font-serif text-xs sm:text-sm italic mt-1"
            style={{ color: attributionColor }}
          >
            Founder, Mind Body Spirit HYGIENE&trade; Tools
          </p>
        </figcaption>
      </div>
    </figure>
  );
};

export default TempleQuote;
